import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  ChevronUp,
  Loader2,
  Search,
  UserCheck,
  UserX,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useCostLimits, useUsageData, useUsers } from '../../hooks/use-admin-api';

/**
 * ユーザー一覧ページ
 */
const LIMIT_OPTIONS = [10, 25, 50] as const;

type SortKey = 'email' | 'status' | 'enabled' | 'cost' | 'limit' | 'createdAt' | 'lastLogin';
type SortDirection = 'asc' | 'desc';

export function UsersPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [limit, setLimit] = useState<number>(25);
  const [currentPage, setCurrentPage] = useState(0);
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const debounceTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

  const { data: usersData, isLoading, error } = useUsers();
  const { data: usageData } = useUsageData();
  const { data: costLimitsData } = useCostLimits();

  // ユーザー別コストのルックアップ（ranking から取得）
  const costByEmail = useMemo(() => {
    const map = new Map<string, number>();
    if (usageData?.ranking) {
      for (const item of usageData.ranking) {
        map.set(item.email, item.cost);
      }
    }
    return map;
  }, [usageData?.ranking]);

  // デフォルト上限
  const defaultLimitUSD = costLimitsData?.defaultLimit?.limitUSD ?? null;

  // ユーザー別個別上限のルックアップ
  const userLimitByEmail = useMemo(() => {
    const map = new Map<string, number>();
    if (costLimitsData?.userLimits) {
      for (const entry of costLimitsData.userLimits) {
        map.set(entry.email, entry.setting.limitUSD);
      }
    }
    return map;
  }, [costLimitsData?.userLimits]);

  // 検索クエリのデバウンス処理（ページもリセット）
  useEffect(() => {
    debounceTimer.current = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setCurrentPage(0);
    }, 300);
    return () => clearTimeout(debounceTimer.current);
  }, [searchQuery]);

  // フィルタリング（部分一致）
  const filteredUsers = useMemo(() => {
    if (!usersData?.users) return [];
    if (!debouncedSearch) return usersData.users;
    const query = debouncedSearch.toLowerCase();
    return usersData.users.filter((user: { email: string }) =>
      user.email.toLowerCase().includes(query),
    );
  }, [usersData?.users, debouncedSearch]);

  // ソート（未指定時は元の順序を維持）
  const sortedUsers = useMemo(() => {
    if (!sortKey) return filteredUsers;
    const dir = sortDirection === 'asc' ? 1 : -1;
    // ソート用の値を取り出す（欠損値は数値 0 として扱う）
    const getSortValue = (user: {
      email: string;
      status: string;
      enabled: boolean;
      createdAt: number | null;
      lastLogin: number | null;
    }): string | number => {
      switch (sortKey) {
        case 'email':
          return user.email;
        case 'status':
          return user.status;
        case 'enabled':
          return user.enabled ? 1 : 0;
        case 'cost':
          return costByEmail.get(user.email) ?? 0;
        case 'limit':
          return userLimitByEmail.get(user.email) ?? defaultLimitUSD ?? 0;
        case 'createdAt':
          return user.createdAt ?? 0;
        case 'lastLogin':
          return user.lastLogin ?? 0;
      }
    };
    return [...filteredUsers].sort((a, b) => {
      const va = getSortValue(a);
      const vb = getSortValue(b);
      if (typeof va === 'string' && typeof vb === 'string') {
        return va.localeCompare(vb, 'ja') * dir;
      }
      return ((va as number) - (vb as number)) * dir;
    });
  }, [filteredUsers, sortKey, sortDirection, costByEmail, userLimitByEmail, defaultLimitUSD]);

  // ヘッダークリックでソート切り替え（昇順 → 降順 → 解除）
  const handleSort = (key: SortKey) => {
    setCurrentPage(0);
    if (sortKey !== key) {
      setSortKey(key);
      setSortDirection('asc');
    } else if (sortDirection === 'asc') {
      setSortDirection('desc');
    } else {
      setSortKey(null);
      setSortDirection('asc');
    }
  };

  // ページネーション
  const totalPages = Math.ceil(sortedUsers.length / limit);
  const currentUsers = useMemo(() => {
    const start = currentPage * limit;
    return sortedUsers.slice(start, start + limit);
  }, [sortedUsers, currentPage, limit]);

  const canGoPrevious = currentPage > 0;
  const canGoNext = currentPage < totalPages - 1;

  const handlePreviousPage = () => {
    if (canGoPrevious) {
      setCurrentPage((p) => p - 1);
    }
  };

  const handleNextPage = () => {
    if (canGoNext) {
      setCurrentPage((p) => p + 1);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400">データの取得に失敗しました</p>
          <p className="text-muted-foreground text-sm mt-2">
            {error instanceof Error ? error.message : 'Unknown error'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* ヘッダー */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-foreground">ユーザー一覧</h1>
          <p className="text-muted-foreground mt-1">
            {filteredUsers.length > 0
              ? `${currentPage * limit + 1} - ${currentPage * limit + currentUsers.length} / ${filteredUsers.length} 件`
              : '0 件'}
          </p>
        </div>

        {/* 検索 */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="メールアドレスで検索..."
            className="w-64 pl-10 pr-10 py-2 bg-card border border-border rounded-lg text-foreground placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* ユーザーテーブル */}
      <div className="bg-card rounded-xl border border-border overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="text-left text-muted-foreground text-sm bg-muted/50">
              <SortableHeader
                label="メールアドレス"
                sortKey="email"
                activeSortKey={sortKey}
                sortDirection={sortDirection}
                onSort={handleSort}
              />
              <SortableHeader
                label="ステータス"
                sortKey="status"
                activeSortKey={sortKey}
                sortDirection={sortDirection}
                onSort={handleSort}
              />
              <SortableHeader
                label="有効"
                sortKey="enabled"
                activeSortKey={sortKey}
                sortDirection={sortDirection}
                onSort={handleSort}
              />
              <SortableHeader
                label="当月使用量"
                sortKey="cost"
                activeSortKey={sortKey}
                sortDirection={sortDirection}
                onSort={handleSort}
                align="right"
              />
              <SortableHeader
                label="実効上限"
                sortKey="limit"
                activeSortKey={sortKey}
                sortDirection={sortDirection}
                onSort={handleSort}
                align="right"
              />
              <SortableHeader
                label="作成日"
                sortKey="createdAt"
                activeSortKey={sortKey}
                sortDirection={sortDirection}
                onSort={handleSort}
              />
              <SortableHeader
                label="最終更新"
                sortKey="lastLogin"
                activeSortKey={sortKey}
                sortDirection={sortDirection}
                onSort={handleSort}
              />
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {currentUsers.length > 0 ? (
              currentUsers.map(
                (user: {
                  email: string;
                  status: string;
                  enabled: boolean;
                  createdAt: number | null;
                  lastLogin: number | null;
                }) => (
                  <tr key={user.email} className="hover:bg-muted/50">
                    <td className="px-6 py-4">
                      <Link
                        to={`/admin/users/${encodeURIComponent(user.email)}`}
                        className="text-foreground hover:text-primary-400 transition-colors font-medium"
                      >
                        {user.email}
                      </Link>
                    </td>
                    <td className="px-6 py-4">
                      <StatusBadge status={user.status} />
                    </td>
                    <td className="px-6 py-4">
                      {user.enabled ? (
                        <span className="flex items-center gap-1 text-green-400">
                          <UserCheck className="w-4 h-4" />
                          有効
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-red-400">
                          <UserX className="w-4 h-4" />
                          無効
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right tabular-nums">
                      <CostCell
                        cost={costByEmail.get(user.email)}
                        limit={userLimitByEmail.get(user.email) ?? defaultLimitUSD}
                      />
                    </td>
                    <td className="px-6 py-4 text-right text-muted-foreground tabular-nums">
                      {(() => {
                        const limit = userLimitByEmail.get(user.email) ?? defaultLimitUSD;
                        return limit !== null ? `$${limit.toFixed(2)}` : '-';
                      })()}
                    </td>
                    <td className="px-6 py-4 text-muted-foreground">
                      {user.createdAt ? new Date(user.createdAt).toLocaleDateString('ja-JP') : '-'}
                    </td>
                    <td className="px-6 py-4 text-muted-foreground">
                      {user.lastLogin ? new Date(user.lastLogin).toLocaleDateString('ja-JP') : '-'}
                    </td>
                  </tr>
                ),
              )
            ) : (
              <tr>
                <td colSpan={7} className="px-6 py-12 text-center text-muted-foreground">
                  ユーザーが見つかりません
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ページネーション */}
      <div className="mt-4 flex items-center justify-end gap-6">
        {/* 表示件数選択 */}
        <div className="flex items-center gap-2 text-sm">
          <span className="text-muted-foreground">表示</span>
          <select
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
            className="px-2 py-1 bg-muted border border-border rounded text-foreground text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
          >
            {LIMIT_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
          <span className="text-muted-foreground">件</span>
        </div>

        {/* ページ送り */}
        <div className="flex items-center">
          <button
            type="button"
            onClick={handlePreviousPage}
            disabled={!canGoPrevious}
            className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-muted-foreground"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <span className="px-2 text-sm text-muted-foreground min-w-[60px] text-center">
            {totalPages > 0 ? `${currentPage + 1} / ${totalPages}` : '-'}
          </span>
          <button
            type="button"
            onClick={handleNextPage}
            disabled={!canGoNext}
            className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-muted-foreground"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * ソート可能なテーブルヘッダー
 * クリックで昇順 → 降順 → 解除を切り替える
 */
function SortableHeader({
  label,
  sortKey,
  activeSortKey,
  sortDirection,
  onSort,
  align = 'left',
}: {
  label: string;
  sortKey: SortKey;
  activeSortKey: SortKey | null;
  sortDirection: SortDirection;
  onSort: (key: SortKey) => void;
  align?: 'left' | 'right';
}) {
  const isActive = activeSortKey === sortKey;
  return (
    <th className={`px-6 py-4 font-medium ${align === 'right' ? 'text-right' : ''}`}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={`flex w-full items-center gap-1 font-medium transition-colors hover:text-foreground ${
          align === 'right' ? 'justify-end' : ''
        } ${isActive ? 'text-foreground' : ''}`}
      >
        {label}
        {isActive ? (
          sortDirection === 'asc' ? (
            <ChevronUp className="w-4 h-4" />
          ) : (
            <ChevronDown className="w-4 h-4" />
          )
        ) : (
          <ChevronsUpDown className="w-4 h-4 opacity-40" />
        )}
      </button>
    </th>
  );
}

/**
 * コスト表示セル
 * 上限に対する使用率で色を変化させる
 */
function CostCell({ cost, limit }: { cost: number | undefined; limit: number | null }) {
  if (cost === undefined || cost === 0) {
    return <span className="text-muted-foreground">-</span>;
  }
  const percent = limit !== null && limit > 0 ? (cost / limit) * 100 : 0;
  const color =
    percent >= 100 ? 'text-red-400' : percent >= 80 ? 'text-yellow-400' : 'text-foreground';
  return <span className={color}>${cost.toFixed(2)}</span>;
}

/**
 * ステータスバッジコンポーネント
 */
function StatusBadge({ status }: { status: string }) {
  const statusConfig: Record<string, { label: string; className: string }> = {
    CONFIRMED: { label: '確認済み', className: 'bg-green-500/20 text-green-400' },
    UNCONFIRMED: { label: '未確認', className: 'bg-yellow-500/20 text-yellow-400' },
    ARCHIVED: { label: 'アーカイブ', className: 'bg-gray-500/20 text-gray-400' },
    COMPROMISED: { label: '侵害', className: 'bg-red-500/20 text-red-400' },
    UNKNOWN: { label: '不明', className: 'bg-gray-500/20 text-gray-400' },
    RESET_REQUIRED: {
      label: 'リセット必要',
      className: 'bg-orange-500/20 text-orange-400',
    },
    FORCE_CHANGE_PASSWORD: {
      label: 'パスワード変更必要',
      className: 'bg-orange-500/20 text-orange-400',
    },
  };

  const config = statusConfig[status] || {
    label: status,
    className: 'bg-gray-500/20 text-gray-400',
  };

  return <span className={`px-2 py-1 text-xs rounded ${config.className}`}>{config.label}</span>;
}
